import { motion } from "framer-motion";

/* ------------------------------------------------------------------ */
/*  The adaptive gap, recreated from the May 2026 framework diagram   */
/*  (Puica, 2026, Technology-Human Equilibrium Framework, Fig 4.3):   */
/*  technological expansion vs human adaptive capacity across three   */
/*  zones: Equilibrium, Emerging Imbalance, Critical Disequilibrium.  */
/* ------------------------------------------------------------------ */

const TECH_D = "M60,358 C 240,318 400,195 560,70";
const ADAPT_LEFT_D = "M60,358 C 180,352 250,346 310,338";
const ADAPT_RIGHT_D = "M310,338 C 400,318 480,285 560,252";

const ZONES = [
  { x: 60, w: 200, fill: "#1E4D38", label: ["Equilibrium", "Zone"], lx: 78, ly: 108 },
  { x: 260, w: 160, fill: "#d4d4d8", label: ["Emerging", "Imbalance"], lx: 282, ly: 300 },
  { x: 420, w: 140, fill: "#f87171", label: ["Critical", "Disequilibrium"], lx: 432, ly: 318 },
];

const COMPLEXITY_NODES = [
  [170, 320],
  [280, 268],
  [390, 205],
  [490, 125],
];

export function AdaptiveGapVisual({ phase = null, assemble = false, mobile = false, className = "" }) {
  const zoneActive = phase !== null && phase >= 3 ? phase - 3 : -1;

  const zoneFs = mobile ? 15 : 10;
  const axisFs = mobile ? 12 : 9;
  const gapFs = mobile ? 13 : 9;
  const zoneLs = mobile ? 1.2 : 1.8;

  const pathProps = assemble
    ? { initial: { pathLength: 0 }, animate: { pathLength: 1 } }
    : {};

  return (
    <svg
      viewBox="0 0 640 420"
      className={className}
      role="img"
      aria-label="Diagram showing technological expansion outpacing human adaptive capacity across three zones: equilibrium, emerging imbalance and critical disequilibrium"
    >
      {/* zone bands */}
      {ZONES.map((z, i) => (
        <motion.rect
          key={i}
          x={z.x}
          y={52}
          width={z.w}
          height={308}
          fill={z.fill}
          initial={{ opacity: 0 }}
          animate={{
            opacity: zoneActive === -1 ? 0.06 : zoneActive === i ? 0.16 : 0.03,
          }}
          transition={{ duration: 0.8, ease: "easeOut", delay: assemble ? 0.9 + i * 0.15 : 0 }}
        />
      ))}
      {/* zone labels */}
      {ZONES.map((z, i) => (
        <motion.text
          key={`l${i}`}
          x={z.lx}
          y={z.ly}
          fontSize={zoneFs}
          letterSpacing={zoneLs}
          animate={{
            fill: zoneActive === i ? "#0F0F0F" : "#44423C",
            opacity: zoneActive === -1 || zoneActive === i ? 1 : 0.55,
          }}
          transition={{ duration: 0.6 }}
        >
          {z.label[0].toUpperCase()}
          <tspan x={z.lx} dy={mobile ? 20 : 14}>{z.label[1].toUpperCase()}</tspan>
        </motion.text>
      ))}

      {/* axes */}
      <line x1="60" y1="52" x2="60" y2="360" stroke="#0F0F0F" strokeOpacity="0.35" strokeWidth="1" />
      <line x1="60" y1="360" x2="560" y2="360" stroke="#0F0F0F" strokeOpacity="0.35" strokeWidth="1" />
      <text x="34" y="210" fontSize={axisFs} letterSpacing="2" fill="#6B6A65" transform="rotate(-90 34 210)" textAnchor="middle">
        HUMAN ADAPTIVE CAPACITY
      </text>
      <text x="310" y="392" fontSize={axisFs} letterSpacing="2" fill="#6B6A65" textAnchor="middle">
        TECHNOLOGICAL EXPANSION
      </text>

      {/* human adaptive capacity: dashed green */}
      <motion.path
        d={ADAPT_LEFT_D}
        fill="none"
        stroke="#1E4D38"
        strokeWidth="1.5"
        strokeDasharray="5 5"
        {...pathProps}
        transition={assemble ? { duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] } : undefined}
      />
      <motion.path
        d={ADAPT_RIGHT_D}
        fill="none"
        stroke="#1E4D38"
        strokeWidth="1.5"
        strokeDasharray="5 5"
        {...(assemble
          ? { initial: { pathLength: 0, opacity: 1 }, animate: { pathLength: 1, opacity: 1 } }
          : { initial: false, animate: { opacity: phase === 2 ? 0.18 : 1 } })}
        transition={{ duration: 1.0, delay: assemble ? 0.7 : 0, ease: [0.22, 1, 0.36, 1] }}
      />

      {/* technological expansion: solid ink */}
      <motion.path
        d={TECH_D}
        fill="none"
        stroke="#0F0F0F"
        strokeWidth="2"
        {...pathProps}
        transition={assemble ? { duration: 1.3, delay: 0.25, ease: [0.22, 1, 0.36, 1] } : undefined}
      />

      {/* SPEED phase: two dots travelling at different paces */}
      <motion.g
        initial={false}
        animate={{ opacity: phase === 0 ? 1 : 0 }}
        transition={{ duration: 0.5 }}
      >
        <circle r="3.5" fill="#0F0F0F">
          <animateMotion dur="2.2s" repeatCount="indefinite" path={TECH_D} />
        </circle>
        <circle r="3" fill="#1E4D38">
          <animateMotion dur="5.5s" repeatCount="indefinite" path={`${ADAPT_LEFT_D} C 400,318 480,285 560,252`} />
        </circle>
      </motion.g>

      {/* COMPLEXITY phase: extra nodes and relationships enter the system */}
      <motion.g
        initial={false}
        animate={{ opacity: phase === 1 ? 1 : 0 }}
        transition={{ duration: 0.7 }}
      >
        {COMPLEXITY_NODES.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="2.5" fill="#0F0F0F" fillOpacity="0.7" />
        ))}
        {[0, 1, 2].map((i) => (
          <line
            key={i}
            x1={COMPLEXITY_NODES[i][0]}
            y1={COMPLEXITY_NODES[i][1]}
            x2={COMPLEXITY_NODES[i + 1][0]}
            y2={COMPLEXITY_NODES[i + 1][1]}
            stroke="#0F0F0F"
            strokeOpacity="0.2"
            strokeWidth="1"
            strokeDasharray="2 4"
          />
        ))}
      </motion.g>

      {/* the adaptive gap connector, visible as the gap becomes the subject */}
      <motion.g
        initial={false}
        animate={{ opacity: phase !== null && phase >= 4 ? (phase === 5 ? 1 : 0.55) : 0 }}
        transition={{ duration: 0.8 }}
      >
        <line x1="524" y1="102" x2="524" y2="258" stroke="#1E4D38" strokeWidth="1" strokeDasharray="3 4" />
        <circle cx="524" cy="102" r="2.5" fill="#0F0F0F" />
        <circle cx="524" cy="258" r="2.5" fill="#1E4D38" />
        <text x="534" y="185" fontSize={gapFs} letterSpacing="1.5" fill="#1E4D38" textAnchor="end" transform="rotate(-90 534 185)">
          THE ADAPTIVE GAP
        </text>
      </motion.g>

      {/* legend */}
      <g>
        <line x1={mobile ? 70 : 430} y1="20" x2={mobile ? 98 : 458} y2="20" stroke="#0F0F0F" strokeWidth="2" />
        <text x={mobile ? 104 : 464} y="24" fontSize={mobile ? 12 : 9} letterSpacing="1.5" fill="#6B6A65">TECHNOLOGICAL EXPANSION</text>
        <line x1={mobile ? 70 : 430} y1="38" x2={mobile ? 98 : 458} y2="38" stroke="#1E4D38" strokeWidth="1.5" strokeDasharray="4 4" />
        <text x={mobile ? 104 : 464} y="42" fontSize={mobile ? 12 : 9} letterSpacing="1.5" fill="#6B6A65">HUMAN ADAPTIVE CAPACITY</text>
      </g>
    </svg>
  );
}
